import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { deleteProfile, getProfile, updateProfile } from '@/features/profiles/lib/profileQueries';
import { PALETTE, initialsFromName, pickAvatarColor } from '@/features/profiles/lib/avatarColors';
import { supabase } from '@/lib/supabase';
import type { BloodType, Gender, Profile, Relation } from '@/types/db';
import clsx from 'clsx';

const RELATION_OPTIONS: { value: Relation; label: string }[] = [
  { value: 'self', label: 'Myself' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'grandmother', label: 'Grandmother' },
  { value: 'grandfather', label: 'Grandfather' },
  { value: 'daughter', label: 'Daughter' },
  { value: 'son', label: 'Son' },
  { value: 'sister', label: 'Sister' },
  { value: 'other', label: 'Other…' },
];

const BLOOD_TYPES: BloodType[] = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

function calcBmi(w: number, h: number) { return w / ((h / 100) ** 2); }

function getBmiInfo(bmi: number): { label: string; classes: string } {
  if (bmi < 18.5) return { label: 'Underweight', classes: 'bg-blue-50 text-flag-info' };
  if (bmi < 25)   return { label: 'Normal weight', classes: 'bg-evt-vax-soft text-evt-vax-ink' };
  if (bmi < 30)   return { label: 'Overweight', classes: 'bg-amber-50 text-flag-warn' };
  return             { label: 'Obese', classes: 'bg-red-50 text-flag-block' };
}

function FieldIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round"
      strokeLinejoin="round" aria-hidden="true" className="shrink-0">
      {children}
    </svg>
  );
}

function OptionalBadge() {
  return (
    <span className="ml-1 inline-flex items-center rounded-full bg-canvas border border-border
      px-1.5 py-0.5 text-[10px] font-normal text-ink-subtle leading-none">
      optional
    </span>
  );
}

function StyledSelect({ id, value, onChange, children }: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative">
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}
        className="input appearance-none pr-10 cursor-pointer">
        {children}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-ink-muted">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round"
          strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
    </div>
  );
}

// ── Load states ───────────────────────────────────────────────

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; profile: Profile }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

// ── Page ──────────────────────────────────────────────────────

export function EditProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loadState, setLoadState] = useState<LoadState>({ kind: 'loading' });

  const [name, setName] = useState('');
  const [relation, setRelation] = useState<Relation>('other');
  const [relationOther, setRelationOther] = useState('');
  const [dob, setDob] = useState('');
  const [avatarColor, setAvatarColor] = useState<string | null>(null);
  const [bloodType, setBloodType] = useState<BloodType | ''>('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [weightKg, setWeightKg] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [allergies, setAllergies] = useState<string[]>([]);
  const [allergyInput, setAllergyInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmEnabled, setConfirmEnabled] = useState(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!id) return;
    getProfile(id)
      .then((profile) => {
        if (!profile) { setLoadState({ kind: 'missing' }); return; }
        setName(profile.name);
        setRelation(profile.relation ?? 'other');
        setRelationOther(profile.relation_other ?? '');
        setDob(profile.date_of_birth ?? '');
        setAllergies(profile.allergies);
        setAvatarColor(profile.avatar_color);
        setBloodType(profile.blood_type ?? '');
        setWeightKg(profile.weight_kg != null ? String(profile.weight_kg) : '');
        setHeightCm(profile.height_cm != null ? String(profile.height_cm) : '');
        setGender(profile.gender ?? '');
        setLoadState({ kind: 'ready', profile });
      })
      .catch((err) => {
        console.error('[edit-profile] load failed', err);
        setLoadState({ kind: 'error', message: "Couldn't load profile." });
      });
  }, [id]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setCurrentUserId(data.user?.id ?? null);
    });
  }, []);

  useEffect(() => {
    if (!showDeleteConfirm) {
      setConfirmEnabled(false);
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
      return;
    }
    confirmTimerRef.current = setTimeout(() => setConfirmEnabled(true), 1500);
    return () => {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    };
  }, [showDeleteConfirm]);

  const resolvedColor = avatarColor ?? pickAvatarColor(name.trim() || undefined);
  const rawBmi = weightKg && heightCm
    ? calcBmi(parseFloat(weightKg), parseFloat(heightCm)) : null;
  const bmiBadge = rawBmi !== null && !isNaN(rawBmi) && isFinite(rawBmi)
    ? getBmiInfo(rawBmi) : null;

  function commitAllergy() {
    const v = allergyInput.trim();
    if (v && !allergies.includes(v)) setAllergies([...allergies, v]);
    setAllergyInput('');
  }
  function removeAllergy(a: string) { setAllergies(allergies.filter(x => x !== a)); }
  function handleAllergyKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commitAllergy(); }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSubmitting(true);
    try {
      await updateProfile(id, {
        name: name.trim(),
        relation,
        relation_other: relation === 'other' ? (relationOther.trim() || null) : null,
        date_of_birth: dob || null,
        allergies,
        avatar_color: resolvedColor,
        blood_type: bloodType || null,
        weight_kg: weightKg ? parseFloat(weightKg) : null,
        height_cm: heightCm ? parseFloat(heightCm) : null,
        gender: gender || null,
      });
      navigate(`/profile/${id}`, { replace: true });
    } catch (err) {
      console.error('[edit-profile] update failed', err);
      setError("Couldn't save changes. Please try again.");
      setSubmitting(false);
    }
  }

  const isOwner =
    loadState.kind === 'ready' &&
    currentUserId !== null &&
    currentUserId === loadState.profile.owner_id;

  async function handleDelete() {
    if (!id) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteProfile(id);
      navigate('/', { replace: true });
    } catch (err) {
      console.error('[edit-profile] delete failed', err);
      setDeleteError("Couldn't delete profile. Please try again.");
      setDeleting(false);
    }
  }

  // ── Loading skeleton ──
  if (loadState.kind === 'loading') {
    return (
      <main className="min-h-screen safe-top">
        <header className="sticky top-0 z-10 flex items-center gap-1 px-2 py-2
          border-b border-border bg-surface">
          <div className="h-11 w-11 rounded-lg bg-canvas animate-pulse" />
          <div className="h-4 w-28 rounded bg-canvas animate-pulse ml-1" />
        </header>
        <section className="px-4 py-5 max-w-md mx-auto space-y-4">
          <div className="card p-5 h-64 bg-canvas animate-pulse" />
          <div className="card p-5 h-48 bg-canvas animate-pulse" />
        </section>
      </main>
    );
  }

  if (loadState.kind === 'missing') {
    return (
      <main className="min-h-screen safe-top flex items-center justify-center p-6">
        <div className="card p-6 text-center max-w-xs w-full">
          <p className="font-medium text-ink mb-3">Profile not found</p>
          <Link to="/home" className="btn-secondary inline-flex">Back to family</Link>
        </div>
      </main>
    );
  }

  if (loadState.kind === 'error') {
    return (
      <main className="min-h-screen safe-top flex items-center justify-center p-6">
        <div className="card p-6 text-center max-w-xs w-full">
          <p className="text-flag-block mb-3">{loadState.message}</p>
          <Link to={`/profile/${id}`} className="btn-secondary inline-flex">Go back</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen safe-top">
      {/* ── Header ── */}
      <header className="sticky top-0 z-10 flex items-center gap-1 px-2 py-2
        border-b border-border bg-surface">
        <Link to={`/profile/${id}`} aria-label="Back"
          className="inline-flex items-center justify-center h-11 w-11 rounded-lg
            text-ink-muted hover:text-ink hover:bg-canvas transition-colors">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true">
            <path d="M12.5 15L7.5 10L12.5 5" />
          </svg>
        </Link>
        <h1 className="text-base font-bold text-ink">Edit profile</h1>
      </header>

      <section className="px-4 py-5 max-w-md mx-auto pb-32">
        <form id="edit-profile-form" onSubmit={handleSubmit} className="space-y-4">

          {/* ══ Card 1: Avatar + Basic Info ══ */}
          <div className="card p-5">

            {/* Avatar preview */}
            <div className="flex flex-col items-center mb-5">
              <div
                className="h-20 w-20 rounded-full flex items-center justify-center text-white
                  font-bold text-2xl shadow-md mb-4 select-none"
                style={{ background: resolvedColor }}
                aria-hidden="true"
              >
                {initialsFromName(name || '–')}
              </div>

              <div className="grid grid-cols-4 gap-2 w-full">
                {PALETTE.map((color, i) => {
                  const sel = resolvedColor === color;
                  return (
                    <button key={color} type="button"
                      onClick={() => setAvatarColor(color)}
                      aria-label={`Color ${i + 1}`} aria-pressed={sel}
                      className="relative h-11 rounded-xl transition-all focus:outline-none
                        focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                      style={{
                        background: color,
                        transform: sel ? 'scale(0.88)' : 'scale(1)',
                        boxShadow: sel ? '0 0 0 3px #fff,0 0 0 5px #FF5A5F' : 'none',
                      }}
                    >
                      {sel && (
                        <svg className="absolute inset-0 m-auto" width="18" height="18"
                          viewBox="0 0 18 18" fill="none" stroke="white" strokeWidth="2.5"
                          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M3.5 9L7 12.5L14.5 5.5" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-ink-subtle mt-2">Pick an avatar color</p>
            </div>

            <hr className="border-border mb-5" />

            <div className="space-y-4">

              {/* Name */}
              <div>
                <label htmlFor="name" className="label">
                  <span className="inline-flex items-center gap-1.5">
                    <FieldIcon>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </FieldIcon>
                    Name
                  </span>
                </label>
                <input id="name" type="text" required autoFocus
                  value={name} onChange={(e) => setName(e.target.value)}
                  className="input" placeholder="e.g. Mona Hassan" />
              </div>

              {/* Relation */}
              <div>
                <label htmlFor="relation" className="label">
                  <span className="inline-flex items-center gap-1.5">
                    <FieldIcon>
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </FieldIcon>
                    Relation
                  </span>
                </label>
                <StyledSelect id="relation" value={relation}
                  onChange={(v) => setRelation(v as Relation)}>
                  {RELATION_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </StyledSelect>
                {relation === 'other' && (
                  <input type="text" value={relationOther}
                    onChange={(e) => setRelationOther(e.target.value)}
                    className="input mt-2"
                    placeholder="e.g. Uncle, Cousin, Caretaker…" />
                )}
              </div>

              {/* Date of birth */}
              <div>
                <label htmlFor="dob" className="label">
                  <span className="inline-flex items-center gap-1.5">
                    <FieldIcon>
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </FieldIcon>
                    Date of birth
                  </span>
                </label>
                <input id="dob" type="date" value={dob}
                  onChange={(e) => setDob(e.target.value)} className="input" />
              </div>

              {/* Gender */}
              <div>
                <p className="label">
                  <span className="inline-flex items-center gap-1.5">
                    <FieldIcon>
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <circle cx="8.5" cy="11" r="2" />
                      <path d="M13 9h5M13 13h5M6 17c0-1.7 1.1-2.5 2.5-2.5S11 15.3 11 17" />
                    </FieldIcon>
                    Gender
                  </span>
                </p>
                <div className="flex gap-2" role="group" aria-label="Gender">
                  {GENDER_OPTIONS.map((opt) => (
                    <button key={opt.value} type="button"
                      aria-pressed={gender === opt.value}
                      onClick={() => setGender(gender === opt.value ? '' : opt.value)}
                      className={clsx(
                        'flex-1 h-11 rounded-xl border text-sm font-medium transition-colors',
                        gender === opt.value
                          ? 'bg-accent text-white border-accent'
                          : 'bg-surface text-ink border-border hover:border-accent/60',
                      )}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

            </div>
          </div>

          {/* ══ Card 2: Health Info ══ */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-bold text-ink">Health info</p>
              <OptionalBadge />
            </div>
            <p className="text-xs text-ink-subtle mb-5">Visible to doctors and caregivers.</p>

            <div className="space-y-5">

              {/* Blood type */}
              <div>
                <label htmlFor="blood-type" className="label">
                  <span className="inline-flex items-center gap-1.5">
                    <FieldIcon>
                      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
                    </FieldIcon>
                    Blood type
                  </span>
                </label>
                <StyledSelect id="blood-type" value={bloodType}
                  onChange={(v) => setBloodType(v as BloodType | '')}>
                  <option value="">Not specified</option>
                  {BLOOD_TYPES.map((bt) => (
                    <option key={bt} value={bt}>{bt}</option>
                  ))}
                </StyledSelect>
              </div>

              {/* Weight + Height */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="weight" className="label">
                    <span className="inline-flex items-center gap-1.5">
                      <FieldIcon>
                        <line x1="12" y1="3" x2="12" y2="21" />
                        <line x1="3" y1="8" x2="21" y2="8" />
                        <line x1="6" y1="8" x2="6" y2="13" />
                        <line x1="18" y1="8" x2="18" y2="13" />
                        <path d="M3 13h6M15 13h6M8 21h8" />
                      </FieldIcon>
                      Weight
                    </span>
                  </label>
                  <div className="relative">
                    <input id="weight" type="number" min="1" max="500" step="0.1"
                      value={weightKg} onChange={(e) => setWeightKg(e.target.value)}
                      className="input pr-10" placeholder="70" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs
                      text-ink-subtle pointer-events-none select-none">kg</span>
                  </div>
                </div>
                <div>
                  <label htmlFor="height" className="label">
                    <span className="inline-flex items-center gap-1.5">
                      <FieldIcon>
                        <path d="M12 3v18M9 6l3-3 3 3M9 18l3 3 3-3" />
                      </FieldIcon>
                      Height
                    </span>
                  </label>
                  <div className="relative">
                    <input id="height" type="number" min="30" max="300" step="0.1"
                      value={heightCm} onChange={(e) => setHeightCm(e.target.value)}
                      className="input pr-10" placeholder="170" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs
                      text-ink-subtle pointer-events-none select-none">cm</span>
                  </div>
                </div>
              </div>

              {/* BMI banner */}
              {bmiBadge && rawBmi !== null && (
                <div className={clsx('flex items-center gap-4 rounded-2xl px-4 py-3', bmiBadge.classes)}>
                  <span className="text-3xl font-bold tabular-nums">{rawBmi.toFixed(1)}</span>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-widest opacity-60">BMI</p>
                    <p className="text-sm font-semibold">{bmiBadge.label}</p>
                  </div>
                </div>
              )}

              {/* Allergies chip input */}
              <div>
                <p className="label">
                  <span className="inline-flex items-center gap-1.5">
                    <FieldIcon>
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </FieldIcon>
                    Allergies
                    <OptionalBadge />
                  </span>
                </p>

                {allergies.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {allergies.map((a) => (
                      <span key={a}
                        className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200
                          text-amber-800 text-xs font-medium px-2.5 py-1 rounded-full">
                        {a}
                        <button type="button" onClick={() => removeAllergy(a)}
                          aria-label={`Remove ${a}`}
                          className="text-amber-400 hover:text-amber-700 transition-colors ml-0.5">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                            aria-hidden="true">
                            <path d="M18 6L6 18M6 6l12 12" />
                          </svg>
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="relative">
                  <input id="allergies-input" type="text"
                    value={allergyInput}
                    onChange={(e) => setAllergyInput(e.target.value)}
                    onKeyDown={handleAllergyKey}
                    onBlur={() => allergyInput.trim() && commitAllergy()}
                    className="input pr-16"
                    placeholder="e.g. Penicillin" />
                  <button type="button" onClick={commitAllergy}
                    disabled={!allergyInput.trim()}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 px-2.5 rounded-lg
                      bg-accent text-white text-xs font-semibold disabled:opacity-30 transition-opacity">
                    Add
                  </button>
                </div>
                <p className="text-xs text-ink-subtle mt-1">
                  Press Enter or comma to add each item.
                </p>
              </div>

            </div>
          </div>

        </form>

        {/* ── Danger zone ── */}
        {isOwner && (
          <div className="mt-4 pt-6 border-t border-red-100">
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full min-h-[44px] py-3 text-sm font-medium text-red-600
                border border-red-200 rounded-xl bg-white active:bg-red-50 transition-colors">
              Delete this profile
            </button>
          </div>
        )}
      </section>

      {/* ── Sticky submit bar ── */}
      <div className="sticky bottom-0 bg-surface/95 backdrop-blur-sm border-t border-border
        px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
        {error && (
          <div role="alert" className="flex items-start gap-2 rounded-xl bg-red-50
            border border-red-200 px-4 py-3 text-sm text-flag-block mb-3">
            <svg className="mt-0.5 shrink-0" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}
        <div className="flex items-center justify-end gap-3">
          <Link
            to={`/profile/${id}`}
            className="text-sm font-medium text-ink-muted hover:text-ink min-h-[44px] flex items-center px-2"
          >
            Cancel
          </Link>
          <button form="edit-profile-form" type="submit"
            disabled={submitting || !name.trim()}
            className="btn-primary px-6 gap-2">
            {submitting && (
              <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
            {submitting ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
      {/* ── Delete confirmation bottom sheet ── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          {/* Backdrop — intentionally not dismissible (design-lead: dangerous action) */}
          <div className="absolute inset-0 bg-black/40" aria-hidden="true" />

          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-confirm-title"
            className="relative bg-surface rounded-t-2xl px-4 pt-6
              pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))]
              shadow-xl w-full max-w-lg mx-auto">

            {/* Title */}
            <h2 id="delete-confirm-title"
              className="text-lg font-bold text-ink mb-2">
              Delete {name}'s profile?
            </h2>

            {/* Body */}
            <p className="text-sm text-ink-muted leading-relaxed mb-5">
              This will permanently erase all records for {name} — prescriptions,
              photos, medications, and chat history.
              <br /><br />
              <span className="font-medium text-ink">This cannot be undone.</span>
            </p>

            {/* Error */}
            {deleteError && (
              <div role="alert"
                className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200
                  px-4 py-3 text-sm text-flag-block mb-4">
                <svg className="mt-0.5 shrink-0" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{deleteError}</span>
              </div>
            )}

            {/* Confirm — destructive, full width, disabled for 1.5 s */}
            <button
              type="button"
              onClick={handleDelete}
              disabled={!confirmEnabled || deleting}
              className="w-full min-h-[44px] py-3 rounded-xl bg-red-600 text-white
                text-sm font-semibold disabled:opacity-40 transition-opacity
                flex items-center justify-center gap-2">
              {deleting && (
                <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                  aria-hidden="true">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
              )}
              {deleting ? 'Deleting…' : 'Yes, delete everything'}
            </button>

            {/* Cancel — below destructive so thumbs don't accidentally hit it first */}
            <button
              type="button"
              onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
              disabled={deleting}
              className="btn-secondary w-full mt-3 min-h-[44px]">
              Cancel, keep profile
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
